import { ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { Component, useEffect, type ErrorInfo, type ReactNode } from "react";
import { Shell } from "./components/Shell.js";
import { findTheme, findVersion } from "./data/catalog.js";
import { parseRoute } from "./lib/routes.js";
import { DirectoryPage } from "./pages/DirectoryPage.js";
import { InstallGuide, ReportGuide, SubmitGuide } from "./pages/GuidePages.js";
import { LocaleGate, NotFoundPage } from "./pages/NotFoundPage.js";
import { ThemePage } from "./pages/ThemePage.js";
import { VersionPage } from "./pages/VersionPage.js";

interface AppProps {
  readonly path?: string;
}

function titleForRoute(path: string): string {
  const route = parseRoute(path);
  if (route.kind === "theme" || route.kind === "version") {
    const theme = findTheme(route.themeId);
    if (theme) return `${theme.localized[route.locale].name} · OpenChatGPTSkin Community`;
  }
  const titles = {
    themes: { "zh-CN": "社区主题目录", en: "Community themes" },
    install: { "zh-CN": "导入指南", en: "Install guide" },
    submit: { "zh-CN": "投稿指南", en: "Submission guide" },
    report: { "zh-CN": "举报指南", en: "Reporting guide" },
  } as const;
  if (route.kind === "themes") return `${titles.themes[route.locale]} · OpenChatGPTSkin`;
  if (route.kind === "guide") return `${titles[route.page][route.locale]} · OpenChatGPTSkin`;
  return "OpenChatGPTSkin Community";
}

function RoutedApp({ path }: { readonly path: string }) {
  const route = parseRoute(path);
  useEffect(() => {
    document.documentElement.lang = route.kind === "locale-gate" ? "zh-CN" : route.locale;
    document.title = titleForRoute(path);
  }, [path, route]);
  if (route.kind === "locale-gate") return <LocaleGate />;
  let page: ReactNode;
  switch (route.kind) {
    case "themes": page = <DirectoryPage locale={route.locale} />; break;
    case "theme": {
      const theme = findTheme(route.themeId);
      page = theme ? <ThemePage locale={route.locale} theme={theme} /> : <NotFoundPage locale={route.locale} />;
      break;
    }
    case "version": {
      const theme = findTheme(route.themeId);
      const version = findVersion(route.themeId, route.version);
      page = theme && version
        ? <VersionPage locale={route.locale} theme={theme} version={version} />
        : <NotFoundPage locale={route.locale} />;
      break;
    }
    case "guide":
      page = route.page === "install" ? <InstallGuide locale={route.locale} />
        : route.page === "submit" ? <SubmitGuide locale={route.locale} />
          : <ReportGuide locale={route.locale} />;
      break;
    case "not-found": page = <NotFoundPage locale={route.locale} />; break;
  }
  return <Shell locale={route.locale} route={route}>{page}</Shell>;
}

interface ErrorBoundaryState { readonly error: Error | null }

class ErrorBoundary extends Component<{ readonly children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): ErrorBoundaryState { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("COMMUNITY_SITE_RENDER_FAILED", error, info.componentStack);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="runtime-error" role="alert">
        <WarningCircle aria-hidden="true" weight="thin" />
        <h1>页面无法显示 · Page unavailable</h1>
        <p>本地页面发生明确错误，没有回退到未经校验的数据。A local rendering error occurred; no unvalidated fallback was used.</p>
        <button type="button" onClick={() => window.location.reload()}><ArrowClockwise aria-hidden="true" />重新加载 · Reload</button>
      </main>
    );
  }
}

export function App({ path }: AppProps) {
  const location = path ?? (typeof window === "undefined" ? "/" : window.location.pathname);
  return <ErrorBoundary><RoutedApp path={location} /></ErrorBoundary>;
}
